import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { productService } from "../services/api";
import { getImageUrl, getBackendImageUrl } from "../utils/imageHelper";
import ProductImageGallery from "../components/common/ProductImageGallery";
import ProductCard from "../components/common/ProductCard";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { Heart, ShoppingCart, Truck, Shield, RotateCcw, Loader, AlertCircle, ChevronDown } from "lucide-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const imageRef = useRef(null);
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [variations, setVariations] = useState([]);
  const [selectedVariations, setSelectedVariations] = useState({}); // Maps variation_type -> selected variation
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await productService.getById(id);
        setProduct(res.data);
        setError(null);
        // Check if product is in wishlist
        setIsFavorite(isInWishlist(res.data.id));
        
        // Fetch variations for this product
        fetchVariations(res.data.id);
        
        // Fetch related products from same category
        if (res.data.category_id) {
          fetchRelatedProducts(res.data.category_id);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, isInWishlist]);

  const fetchVariations = async (productId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/variations/${productId}`);
      setVariations(response.data || []);
      // Don't auto-select first variation - let user select manually
    } catch (err) {
      console.error("Error fetching variations:", err);
    }
  };

  const fetchRelatedProducts = async (categoryId) => {
    try {
      // Fetch all products from the same category
      const response = await axios.get(`${API_BASE_URL}/products`);
      if (response.data) {
        // Filter products from same category, exclude current product
        const related = response.data
          .filter(p => p.category_id === categoryId && p.id !== parseInt(id))
          .slice(0, 6); // Show up to 6 related products
        setRelatedProducts(related);
      }
    } catch (err) {
      console.error("Error fetching related products:", err);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    setShowSuccess(true);
    addToCart(product, Number(quantity || 1), selectedVariations);
    setTimeout(() => {
      setShowSuccess(false);
    }, 2500);
  };

  const handleToggleWishlist = () => {
    if (!product) return;
    if (isFavorite) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
    setIsFavorite(!isFavorite);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <Loader className="h-10 sm:h-12 w-10 sm:w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-base sm:text-lg text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="h-10 sm:h-12 w-10 sm:w-12 text-red-600 mx-auto mb-4" />
          <p className="text-base sm:text-lg text-red-600 mb-4 font-semibold">{error || "Product not found"}</p>
          <button
            onClick={() => navigate("/shop")}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  const imageUrl = getImageUrl(product.image);

  return (
    <div className="min-h-screen ">
      {/* Breadcrumb Navigation */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors text-sm sm:text-base"
          >
            ← Back to Shop
          </button>
        </div>
      </div>

      {/* Main Product Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Image Section - Using Gallery Component */}
            <div ref={imageRef} className="flex items-start justify-center lg:sticky lg:top-20 lg:h-fit relative">
            {/* Wishlist Button - Top Right Corner */}
            <button
              onClick={handleToggleWishlist}
              className={`absolute top-4 right-4 z-10 p-2 sm:p-3 rounded-full transition-all  ${
                isFavorite
                  ? " text-black-600"
                  : " text-black-600 "
              }`}
              title="Add to wishlist"
            >
              <Heart className={`h-5 sm:h-6 w-5 sm:w-6 ${isFavorite ? "fill-current" : ""}`} />
            </button>
            <div className="w-full">
              {(() => {
                // Get first selected variation with images
                const firstSelectedVariation = Object.values(selectedVariations).find(v => v && v.images && v.images.length > 0);
                
                if (firstSelectedVariation) {
                  return (
                    <ProductImageGallery
                      mainImage={`${API_BASE_URL}/${firstSelectedVariation.images[0].image_path}`}
                      additionalImages={firstSelectedVariation.images.slice(1).map((img) => ({
                        image_path: `${API_BASE_URL}/${img.image_path}`,
                        angle_description: `${firstSelectedVariation.variation_value}`
                      }))}
                    />
                  );
                } else {
                  return (
                    <ProductImageGallery
                      mainImage={product.image}
                      additionalImages={product.additional_images}
                    />
                  );
                }
              })()}
            </div>
          </div>

          {/* Product Details Section */}
          <div className="flex flex-col">
            {/* Product Header */}
            <div className="">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-blue-600 mb-2">PRODUCT ID: {product.id}</p>
                  <h1 className="text-xl sm:text-xl lg:text-xl xl:text-xl font-semibold text-gray-900 break-words">{product.name}</h1>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-sm sm:text-base">★</span>
                  ))}
                </div>
                <span className="text-xs sm:text-sm text-gray-600 font-medium">(326 reviews)</span>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-gray-200 mb-2"></div>

            {/* Price Section */}
            <div className="mb-3 sm:mb-4">
              <div className="flex items-center gap-3 sm:gap-4 mb-2 flex-wrap">
                <span className="text-xl sm:text-xl lg:text-xl font-normal text-black">
                  ₹{(() => {
                    const selectedVars = Object.values(selectedVariations);
                    if (selectedVars.length > 0) {
                      // Sum up all selected variation prices
                      const totalPrice = selectedVars.reduce((sum, v) => sum + Number(v.price_adjustment || 0), 0);
                      // If total is 0, show default product price
                      return totalPrice > 0 ? Math.round(totalPrice) : Math.round(Number(product.price));
                    }
                    return Math.round(Number(product.price));
                  })()}
                </span>
                <span className="inline-block px-2 sm:px-3 py-1 bg-gray-200 text-gray-900 text-xs sm:text-sm font-normal rounded-full">
                  In Stock
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-700">Incl. of all taxes</p>
            </div>

            {/* Variations Section - Grouped by Type */}
            {variations.length > 0 && (
              <div className="mb-6 sm:mb-8 space-y-6">
                {(() => {
                  // Group variations by type
                  const groupedVariations = {};
                  variations.forEach((variation) => {
                    if (!groupedVariations[variation.variation_type]) {
                      groupedVariations[variation.variation_type] = [];
                    }
                    groupedVariations[variation.variation_type].push(variation);
                  });
                  
                  return Object.entries(groupedVariations).map(([type, typeVariations]) => {
                    const selectedForType = selectedVariations[type];
                    const isColorType = type.toLowerCase() === 'color';
                    
                    return (
                      <div key={type}>
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                          <h3 className="text-sm sm:text-base font-normal text-gray-900">Select {type}</h3>
                          {isColorType && <a href="#size-guide" className="text-xs sm:text-sm text-blue-600 hover:underline font-medium">Size Guide</a>}
                        </div>
                        
                        {/* Render based on type */}
                        {isColorType ? (
                          // Color variations - Show as image thumbnails
                          <div className="flex flex-wrap gap-2 sm:gap-3">
                            {typeVariations.map((variation) => (
                              <button
                                key={variation.id}
                                onClick={() => {
                                  if (selectedForType?.id === variation.id) {
                                    const newSelections = { ...selectedVariations };
                                    delete newSelections[type];
                                    setSelectedVariations(newSelections);
                                  } else {
                                    setSelectedVariations({
                                      ...selectedVariations,
                                      [type]: variation
                                    });
                                    // Scroll to image section
                                    setTimeout(() => {
                                      imageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 100);
                                  }
                                }}
                                className={`relative group transition-all duration-300 ${
                                  selectedForType?.id === variation.id ? 'ring-2 ring-offset-2 ring-blue-600' : 'hover:ring-2 hover:ring-offset-2 hover:ring-gray-300'
                                }`}
                                title={variation.variation_value}
                              >
                                {/* Color image thumbnail */}
                                {variation.images && variation.images.length > 0 ? (
                                  <img
                                    src={`${API_BASE_URL}/${variation.images[0].image_path}`}
                                    alt={variation.variation_value}
                                    className="h-12 sm:h-14 w-12 sm:w-14 rounded-lg object-cover border border-gray-300"
                                  />
                                ) : (
                                  <div className="h-12 sm:h-14 w-12 sm:w-14 rounded-lg bg-gray-200 flex items-center justify-center border border-gray-300 text-xs font-semibold text-gray-600">
                                    {variation.variation_value}
                                  </div>
                                )}
                                
                                {/* Label below thumbnail */}
                                <p className="text-xs sm:text-sm font-semibold text-gray-900 text-center mt-2 truncate">
                                  {variation.variation_value}
                                </p>
                              </button>
                            ))}
                          </div>
                        ) : (
                          // Size or other variations - Show as text buttons
                          <div className="flex flex-wrap gap-2 sm:gap-3">
                            {typeVariations.map((variation) => (
                              <button
                                key={variation.id}
                                onClick={() => {
                                  if (selectedForType?.id === variation.id) {
                                    const newSelections = { ...selectedVariations };
                                    delete newSelections[type];
                                    setSelectedVariations(newSelections);
                                  } else {
                                    setSelectedVariations({
                                      ...selectedVariations,
                                      [type]: variation
                                    });
                                    // Scroll to image section
                                    setTimeout(() => {
                                      imageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 100);
                                  }
                                }}
                                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-normal transition-all duration-300 text-xs sm:text-sm border-2 ${
                                  selectedForType?.id === variation.id
                                    ? "bg-black text-white border-black shadow-md"
                                    : "bg-transparent text-gray-900 border-gray-300 hover:border-gray-900"
                                }`}
                              >
                                {variation.variation_value}
                                {/* {variation.price_adjustment > 0 && (
                                  <span className="ml-1 text-xs font-normal">+₹{variation.price_adjustment}</span>
                                )} */}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Quantity and Actions */}
            <div className="mb-6 sm:mb-8">
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">Quantity</label>
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Quantity Selector */}
                <div className="inline-flex items-center gap-2 sm:gap-3 bg-white border border-gray-300 rounded-lg p-1 sm:p-2">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center hover:bg-gray-100 rounded transition-colors font-bold text-gray-600 hover:text-gray-900 text-sm sm:text-base"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-12 sm:w-16 text-center font-semibold text-gray-900 focus:outline-none text-sm sm:text-base"
                    min="1"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center hover:bg-gray-100 rounded transition-colors font-bold text-gray-600 hover:text-gray-900 text-sm sm:text-base"
                  >
                    +
                  </button>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  className="flex-1 px-4 sm:px-6 py-3 sm:py-4 bg-black text-white font-semibold text-sm sm:text-base rounded-lg hover:bg-gray-900 transition-all hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <ShoppingCart className="h-4 sm:h-5 w-4 sm:w-5" />
                  Add to Cart
                </button>
              </div>
            </div>

            {/* Product Details Accordion */}
            <div className="space-y-0  rounded-lg divide-y ">
              {/* Specifications */}
              <div className="border-b border-gray-300">
                <button
                  onClick={() => setExpandedAccordion(expandedAccordion === 'specs' ? null : 'specs')}
                  className="w-full flex items-center justify-between p-4  transition-colors"
                >
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base">Specifications</h4>
                    {/* <p className="text-xs sm:text-sm text-gray-700 mt-0.5">Technical details and features</p> */}
                  </div>
                  <ChevronDown 
                    className={`h-5 w-5 text-gray-900 transition-transform duration-300 flex-shrink-0 ${
                      expandedAccordion === 'specs' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedAccordion === 'specs' && (
                  <div className="px-4 pb-4 text-sm sm:text-base text-gray-800 ">
                    <ul className="space-y-2">
                      <li><span className="font-medium">Availability:</span> <span className="text-gray-900 font-bold">In Stock</span></li>
                      <li><span className="font-medium">Product ID:</span> PROD-{product.id}</li>
                      <li><span className="font-medium">Category:</span> General</li>
                      <li><span className="font-medium">SKU:</span> {product.id}-{Math.random().toString(36).substr(2, 9).toUpperCase()}</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="border-b border-gray-300">
                <button
                  onClick={() => setExpandedAccordion(expandedAccordion === 'desc' ? null : 'desc')}
                  className="w-full flex items-center justify-between p-4  transition-colors"
                >
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base">Description</h4>
                    {/* <p className="text-xs sm:text-sm text-gray-700 mt-0.5">Product overview and details</p> */}
                  </div>
                  <ChevronDown 
                    className={`h-5 w-5 text-gray-900 transition-transform duration-300 flex-shrink-0 ${
                      expandedAccordion === 'desc' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedAccordion === 'desc' && (
                  <div className="px-4 pb-4 text-sm sm:text-base text-gray-800 ">
                    {product.description || "Premium quality product with excellent features and durability."}
                  </div>
                )}
              </div>

              {/* Returns, Exchange, & Refund Policy */}
              <div className="border-b border-gray-300">
                <button
                  onClick={() => setExpandedAccordion(expandedAccordion === 'policy' ? null : 'policy')}
                  className="w-full flex items-center justify-between p-4  transition-colors"
                >
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base">Returns, Exchange, & Refund Policy</h4>
                    {/* <p className="text-xs sm:text-sm text-gray-700 mt-0.5">7 days easy returns and exchange</p> */}
                  </div>
                  <ChevronDown 
                    className={`h-5 w-5 text-gray-900 transition-transform duration-300 flex-shrink-0 ${
                      expandedAccordion === 'policy' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedAccordion === 'policy' && (
                  <div className="px-4 pb-4 text-sm sm:text-base text-gray-800  space-y-3">
                    <p><span className="font-medium">Return Period:</span> 7 days from delivery</p>
                    <p><span className="font-medium">Condition:</span> Product must be unused and in original packaging</p>
                    <p><span className="font-medium">Process:</span> Contact support to initiate return/exchange</p>
                    <p><span className="font-medium">Refund:</span> Refunds processed within 5-7 business days</p>
                  </div>
                )}
              </div>

              {/* Marketed By */}
              <div>
                <button
                  onClick={() => setExpandedAccordion(expandedAccordion === 'seller' ? null : 'seller')}
                  className="w-full flex items-center justify-between p-4  transition-colors"
                >
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base">Marketed By</h4>
                    {/* <p className="text-xs sm:text-sm text-gray-700 mt-0.5">Company and distributor information</p> */}
                  </div>
                  <ChevronDown 
                    className={`h-5 w-5 text-gray-900 transition-transform duration-300 flex-shrink-0 ${
                      expandedAccordion === 'seller' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedAccordion === 'seller' && (
                  <div className="px-4 pb-4 text-sm sm:text-base text-gray-800  space-y-2">
                    <p><span className="font-medium">Seller:</span> Sellerocket Store</p>
                    <p><span className="font-medium">Email:</span> support@sellerocket.com</p>
                    <p><span className="font-medium">Phone:</span> +91-XXXXXXXXXX</p>
                    <p><span className="font-medium">Address:</span> Premium Store, Shopping District</p>
                  </div>
                )}
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-white rounded-lg border border-gray-300">
              <div className="text-center">
                <Truck className="h-5 sm:h-6 w-5 sm:w-6 text-gray-900 mx-auto mb-1 sm:mb-2" />
                <p className="text-xs font-semibold text-gray-900">Free Delivery</p>
                <p className="text-xs text-gray-700 hidden sm:block">On orders ₹500+</p>
              </div>
              <div className="text-center">
                <RotateCcw className="h-5 sm:h-6 w-5 sm:w-6 text-gray-900 mx-auto mb-1 sm:mb-2" />
                <p className="text-xs font-semibold text-gray-900">Easy Return</p>
                <p className="text-xs text-gray-700 hidden sm:block">30-day policy</p>
              </div>
              <div className="text-center">
                <Shield className="h-5 sm:h-6 w-5 sm:w-6 text-gray-900 mx-auto mb-1 sm:mb-2" />
                <p className="text-xs font-semibold text-gray-900">Secure Buy</p>
                <p className="text-xs text-gray-700 hidden sm:block">100% safe</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="bg-white border-t border-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Premium quality materials",
              "Long-lasting durability",
              "Easy to use and maintain",
              "Environmentally friendly"
            ].map((feature, idx) => (
              <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                  ✓
                </span>
                <span className="text-gray-700 font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Product Specifications */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Delivery Info */}
            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Delivery Information</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold mt-1">•</span>
                  <div>
                    <p className="font-semibold text-gray-900">Free Shipping</p>
                    <p className="text-sm text-gray-600">On orders above ₹500</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold mt-1">•</span>
                  <div>
                    <p className="font-semibold text-gray-900">Fast Delivery</p>
                    <p className="text-sm text-gray-600">3-5 business days</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold mt-1">•</span>
                  <div>
                    <p className="font-semibold text-gray-900">Order Tracking</p>
                    <p className="text-sm text-gray-600">Real-time tracking available</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Product Info */}
            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Product Information</h3>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Availability</span>
                  <span className="text-green-600 font-bold">In Stock</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Product ID</span>
                  <span className="text-gray-900 font-semibold">PROD-{product.id}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Category</span>
                  <span className="text-gray-900 font-semibold">General</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600 font-medium">Return Policy</span>
                  <span className="text-gray-900 font-semibold">30 Days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products Section */}
      <div className="bg-white border-t border-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Products</h2>
          {relatedProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-gray-600 font-medium">No related products available in this category</p>
            </div>
          )}
        </div>
      </div>

      {/* Success Popup */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-[999]">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>

          {/* Confetti Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="confetti-container">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  className="confetti"
                  style={{
                    left: Math.random() * 100 + '%',
                    delay: Math.random() * 0.5 + 's',
                    '--duration': (Math.random() * 1.5 + 1.5) + 's',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Success Card */}
          <div className="relative z-10 bg-white rounded-2xl p-8 shadow-2xl max-w-sm text-center animate-popup-appear">
            {/* Celebration Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-full flex items-center justify-center animate-bounce-gentle shadow-lg">
                  <span className="text-4xl">🎉</span>
                </div>
                {/* Shine effect */}
                <div className="absolute inset-0 bg-white rounded-full opacity-20 blur-md"></div>
              </div>
            </div>

            {/* Success Message */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Product Added!
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              {quantity} × {product.name}
            </p>
            
            {/* Subtext */}
            <p className="text-xs text-gray-500 mb-6">
              Item added to your cart successfully
            </p>

            {/* Action Button */}
            <button
              onClick={() => setShowSuccess(false)}
              className="px-6 py-2.5 bg-black text-white font-semibold rounded-lg hover:bg-gray-900 transition-all text-sm"
            >
              Got it, Thanks!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
